import { useEffect, useRef, useState } from "react";
import HTMLFlipBook from "react-pageflip";
import { BookOpen, Download, ChevronLeft, ChevronRight, X } from "lucide-react";
import { FadeIn } from "@/components/FadeIn";
import { Ornament } from "@/components/Ornament";
import { useLanguage } from "@/context/LanguageContext";
import { strings } from "@/i18n";

const PAGE_COUNT = 20;
const PAGE_RATIO = 842.25 / 595.5;
const pages = Array.from(
  { length: PAGE_COUNT },
  (_, i) => `/livret/pages/page-${String(i + 1).padStart(2, "0")}.jpg`
);

const FlipbookViewer = ({ onClose }) => {
  const bookRef = useRef(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState({ w: 320, h: 452, portrait: true });
  const { lang } = useLanguage();
  const s = strings.livret[lang];

  useEffect(() => {
    const compute = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const portrait = vw < 768;
      let w = portrait
        ? Math.min(vw - 56, 420)
        : Math.min(Math.floor((Math.min(vw, 1100) - 120) / 2), 460);
      let h = Math.round(w * PAGE_RATIO);
      const maxH = Math.round(vh * 0.72);
      if (h > maxH) {
        h = maxH;
        w = Math.round(h / PAGE_RATIO);
      }
      setSize({ w, h, portrait });
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  return (
    <div className="mt-6" data-testid="flipbook-viewer">
      <div className="flex items-center justify-between">
        <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-noir/50" data-testid="flipbook-counter">
          Page {Math.min(page + 1, PAGE_COUNT)} / {PAGE_COUNT}
        </p>
        <button
          type="button"
          onClick={onClose}
          data-testid="flipbook-close-button"
          className="inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.25em] text-bordeaux transition-opacity duration-300 hover:opacity-60"
        >
          <X size={14} strokeWidth={1.5} />
          {s.close}
        </button>
      </div>
      <div className="mt-6 flex justify-center" data-testid="flipbook-book">
        <HTMLFlipBook
          ref={bookRef}
          width={size.w}
          height={size.h}
          size="fixed"
          minWidth={260}
          maxWidth={460}
          minHeight={360}
          maxHeight={700}
          showCover={true}
          usePortrait={size.portrait}
          startPage={0}
          drawShadow={true}
          flippingTime={800}
          maxShadowOpacity={0.35}
          mobileScrollSupport={true}
          clickEventForward={true}
          showPageCorners={true}
          disableFlipByClick={false}
          swipeDistance={30}
          autoSize={false}
          className="shadow-[0_30px_60px_-20px_rgba(92,11,19,0.35)]"
          onFlip={(e) => setPage(e.data)}
        >
          {pages.map((src, i) => (
            <div key={src} className="flipbook-page bg-ivory">
              <img
                src={src}
                alt={`Page ${i + 1} du livret Roganel 70 ans`}
                className="h-full w-full object-cover"
                draggable={false}
              />
            </div>
          ))}
        </HTMLFlipBook>
      </div>
      <div className="mt-8 flex items-center justify-center gap-6">
        <button
          type="button"
          onClick={() => bookRef.current?.pageFlip().flipPrev()}
          data-testid="flipbook-prev-button"
          className="inline-flex items-center gap-2 border border-bordeaux/40 px-5 py-3 font-sans text-[10px] uppercase tracking-[0.25em] text-bordeaux transition-[background-color,color] duration-300 hover:bg-bordeaux hover:text-ivory"
        >
          <ChevronLeft size={14} strokeWidth={1.5} />
          {s.prev}
        </button>
        <button
          type="button"
          onClick={() => bookRef.current?.pageFlip().flipNext()}
          data-testid="flipbook-next-button"
          className="inline-flex items-center gap-2 border border-bordeaux/40 px-5 py-3 font-sans text-[10px] uppercase tracking-[0.25em] text-bordeaux transition-[background-color,color] duration-300 hover:bg-bordeaux hover:text-ivory"
        >
          {s.next}
          <ChevronRight size={14} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
};

export default function Livret() {
  const [open, setOpen] = useState(false);
  const viewerRef = useRef(null);
  const { lang } = useLanguage();
  const s = strings.livret[lang];

  const openViewer = () => {
    setOpen(true);
    setTimeout(() => {
      viewerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  return (
    <div data-testid="livret-page">
      <section className="mx-auto max-w-6xl px-6 pt-16 md:px-8 md:pt-24">
        <FadeIn>
          <div className="text-center">
            <p className="eyebrow" data-testid="livret-eyebrow">
              {s.eyebrow}
            </p>
            <h1 className="mt-6 font-serif text-4xl font-medium tracking-[0.12em] text-noir sm:text-5xl md:text-6xl" data-testid="livret-title">
              {s.title}
            </h1>
            <p className="mx-auto mt-6 max-w-lg font-serif text-lg italic leading-relaxed text-noir/70 [text-wrap:pretty] md:text-xl" data-testid="livret-subtitle">
              {s.subtitle}
            </p>
            <div className="mt-10">
              <Ornament />
            </div>
          </div>
        </FadeIn>

        <div className="grid items-center gap-14 py-16 md:grid-cols-2 md:gap-20 md:py-24">
          <FadeIn>
            <div className="double-frame mx-auto max-w-sm md:max-w-none" data-testid="livret-cover-frame">
              <div className="double-frame-inner">
                <img
                  src="/livret/couverture.jpg"
                  alt="Couverture du livret Roganel 70 ans"
                  data-testid="livret-cover-image"
                  className="w-full"
                />
              </div>
            </div>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div className="text-center md:text-left">
              <p className="font-serif text-3xl font-medium leading-snug text-bordeaux md:text-4xl" data-testid="livret-presentation-title">
                {lang === "fr" ? (
                  <>
                    Soixante-dix ans
                    <br />
                    réunis en un livret
                  </>
                ) : (
                  s.presentationTitle
                )}
              </p>
              <p className="mt-6 font-sans text-sm font-light leading-loose text-noir/75 [text-wrap:pretty] md:text-base" data-testid="livret-presentation-text">
                {s.presentationText}
              </p>
              <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row md:items-start md:justify-start sm:justify-center">
                <button type="button" onClick={openViewer} className="btn-primary" data-testid="livret-flipbook-button">
                  <BookOpen size={14} strokeWidth={1.5} />
                  {s.flipbook}
                </button>
                <a
                  href="/livret/livret-roganel-70ans.pdf"
                  download="ROGANEL-Livret-70-ans.pdf"
                  className="btn-outline"
                  data-testid="livret-download-button"
                >
                  <Download size={14} strokeWidth={1.5} />
                  {s.download}
                </a>
              </div>
              {lang === "en" && (
                <p className="mt-8 font-sans text-[11px] uppercase tracking-[0.3em] text-noir/40" data-testid="livret-french-note">
                  {s.frenchNote}
                </p>
              )}
            </div>
          </FadeIn>
        </div>
      </section>

      <section ref={viewerRef} className="border-t border-bordeaux/10 bg-cream/40" data-testid="livret-viewer-section">
        <div className="mx-auto max-w-6xl px-6 py-16 md:px-8 md:py-20">
          {open ? (
            <FlipbookViewer onClose={() => setOpen(false)} />
          ) : (
            <FadeIn>
              <div className="text-center">
                <p className="mx-auto max-w-xl font-serif text-2xl italic text-noir/70 [text-wrap:pretty] md:text-3xl" data-testid="livret-invite-text">
                  {s.invite}
                </p>
                <button type="button" onClick={openViewer} className="btn-outline mt-8" data-testid="livret-open-viewer-button">
                  <BookOpen size={14} strokeWidth={1.5} />
                  {s.openViewer}
                </button>
              </div>
            </FadeIn>
          )}
        </div>
      </section>
    </div>
  );
}
